// 1. React
import { forwardRef, memo, useImperativeHandle, useRef } from "react";

// 2. Helpers
import { useTranslate } from "helpers";
import { useOpenOnTab } from "services/hooks/useOpenOnTab";

// 3. Types
import { VehicleInvoiceCardProps, VehicleInvoiceRef } from "./types";

// 4. UI Components
import { Tabs } from "ui/components/Tabs";
import { NoResults } from "ui/components/NoResults";

// 5. Local Components
import VehicleInvoiceSaleCard from "./components/VehicleInvoiceSaleCard/VehicleInvoiceSaleCard";
import { VehicleInvoiceSaleRef } from "./components/VehicleInvoiceSaleCard/types";
import VehicleInvoiceRentCard from "./components/VehicleInvoiceRentCard/VehicleInvoiceRentCard";
import { VehicleInvoiceRentRef } from "./components/VehicleInvoiceRentCard/types";

const VehicleInvoiceCard = forwardRef<VehicleInvoiceRef, VehicleInvoiceCardProps>((props, ref) => {
  const { fleetVehicleDetail } = props;
  const { t } = useTranslate();

  const vehicleInvoiceSaleRef = useRef<VehicleInvoiceSaleRef>(null);
  const vehicleInvoiceRentRef = useRef<VehicleInvoiceRentRef>(null);

  const { activeTab, setActiveTab, openOnTab } = useOpenOnTab();

  useImperativeHandle(ref, () => ({
    openAddNewSale: () => openOnTab(0, () => vehicleInvoiceSaleRef.current?.openAddNew()),
    openAddNewRent: () => openOnTab(1, () => vehicleInvoiceRentRef.current?.openAddNew()),
    refresh: () => {
      vehicleInvoiceSaleRef.current?.refresh();
      vehicleInvoiceRentRef.current?.refresh();
    },
  }));

  if (!fleetVehicleDetail.id) return <></>;

  return (
    <Tabs
      activeIndex={activeTab}
      onTabChange={setActiveTab}
      tabs={[
        {
          label: t("admin.fleet.vehicle.invoice.sale.tab.search"),
          content: (
            <VehicleInvoiceSaleCard ref={vehicleInvoiceSaleRef} fleetVehicleDetail={fleetVehicleDetail} />
          ),
        },
        {
           label: t("admin.fleet.vehicle.invoice.rent.tab.search"),
          content: (
            <VehicleInvoiceRentCard ref={vehicleInvoiceRentRef} fleetVehicleDetail={fleetVehicleDetail} />
          ),
        },
        {
          label: t("admin.fleet.vehicle.invoice.tab.statistics"),
          content: (
            <div className="p-8 flex items-center justify-center min-h-[200px]">
              <NoResults message="Statistika računa - uskoro dostupno" />
            </div>
          ),
        },
      ]}
    />
  );
});

export default memo(VehicleInvoiceCard);
